import Usercentrics, {
  BaseCategory,
  Category,
  Core as BaseSettings,
  DefaultLabels,
  Labels as SettingsLabels,
  Service,
  TRACKING_EVENT_TYPE,
  UI_LAYER,
  UserDecision,
} from '@usercentrics/cmp-browser-sdk';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  JSX,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import ConsentHelper from './ConsentHelper/ConsentHelper';
import { Constants, Events } from './constants';
import Technologies from './components/cmm-privacy-shield-overlay/Technologies';
import { getSettingData } from './services/settings/getSettingData';
import { getLabels } from './services/labels/getLabels';
import { getLabelsToBanner } from './utils/getLabelsToBanner';
import { getLanguage } from './utils/getLanguage';
import { Direction, getTextDirection } from './utils/getTextDirection';
import { hintConverter } from './utils/hintConverter';
import { stringCleaner } from './utils/stringCleaner';
import { BannerLabels } from './types/BannerLabels';
import { BannerLink } from './types/BannerLink';
import { BannerVariant, DEFAULT_VARIANT } from './types/BannerVariant';
import { ButtonLabels } from './types/ButtonLabels';
import { CheckboxState } from './types/CheckboxState';
import { Label, LabelsResponse, Language, LanguageResponse, Response } from './types/LabelsResponse';
import { LabelsModeled } from './types/LabelsModeled';
import { Technology } from './types/Technology';
import { SettingDataResponse } from './types/SettingDataResponse';
import { ConsentStatus } from './types/ConsentStatus';
import { WorkbenchConfig } from './types/WorkbenchConfig';
import { HeadingContent } from './types/HeadingContent';
import { CookieSettingsLabels } from './types/CookieSettingsLabels';
import { LegalPages } from './types/LegalPages';
import { ConsentByCategory } from './types/ConsentByCategory';
import { LanguageSettings } from './types/LanguageSettings';
import { Container } from './types/Container';
import { PrivacyShieldLabels } from './types/PrivacyShieldLabels';
import { getLabelsToPlaceholder } from './utils/getLabelsToPlaceholder';
import { setFocusFirstElement } from './utils/setFocusFirstElement';
import { addFocusEventListener, removeFocusEventListener } from './utils/toggleFocusEventListener';
import { restoreAriaHiddenAttributes, setAriaHiddenAttributes } from './utils/toggleAriaHiddenAttributes';
import { loadWorkbench, loadWorkbenchCss } from './utils/workbenchUtils';
import { LabelKeysOverrides } from './types/LabelKeysOverrides';

declare global {
  interface Window {
    workbenchConfig?: WorkbenchConfig;
    workbench?: WorkbenchConfig;
  }
}

@Component({
  tag: 'cmm-cookie-banner',
  styleUrl: 'cmm-cookie-banner.scss',
  shadow: true,
})
export class CookieBanner implements ComponentInterface {
  private direction: Direction = Constants.LEFT_TO_RIGHT;
  private firstRender = true;
  private UC: Usercentrics;
  private ucInitialized = false;
  private userDecision: UserDecision[];
  private lastActiveElement: HTMLElement;

  @Element() el: HTMLElement;

  @State() bannerLabels: BannerLabels;
  @State() bannerVisible = false;
  @State() currentLanguage: string = Constants.DEFAULT_LANGUAGE_CODE;
  @State() internalLegalPages: LegalPages;
  @State() internalVariant: BannerVariant;
  @State() labels: Array<LabelsModeled>;
  @State() languages: Array<Language>;
  @State() showNotification = false;

  @State() baseSettings: BaseSettings;
  @State() settingsLabels: SettingsLabels;
  @State() categories: BaseCategory[] = null;

  @Prop() settingsId: string;
  @Prop({ mutable: true, reflect: true }) variant?: BannerVariant;
  @Prop() forceVariant = false;
  @Prop() initialLanguage?: string;
  @Prop() legalPages?: string;

  private legalName = '';

  @Watch('legalPages')
  parseLegalPagesProp(newValue: string): void {
    if (newValue) {
      this.internalLegalPages = JSON.parse(newValue);
      this.getFooterLinks();
    }
  }

  public componentWillLoad(): void {
    this.initializeUC().then(() => this.handleVariantInitialization());

    loadWorkbench();
    loadWorkbenchCss(this.el);

    this.parseLegalPagesProp(this.legalPages);
  }

  public componentWillRender(): Promise<void> {
    if (this.firstRender && this.initialLanguage) {
      return this.UC?.changeLanguage(this.initialLanguage)
        .then()
        .catch()
        .finally(() => {
          this.firstRender = false;
          this.updateContent();
        });
    } else {
      return Promise.resolve(this.updateContent());
    }
  }

  public componentDidRender(): void {
    if (!this.bannerVisible || this.internalVariant === BannerVariant.SettingsPage || !this.el) return;
    this.initializeAriaAttributes();
    requestAnimationFrame(() => {
      if (this.el) {
        setFocusFirstElement(this.el);
      }
    });
  }

  public disconnectedCallback(): void {
    removeFocusEventListener(this.el);
    restoreAriaHiddenAttributes();
  }

  @Listen('usercentrics-change-language', { target: 'document', capture: true })
  ucChangeLanguage(): void {
    this.updateContent();
  }

  @Listen('privacy-shield-show-modal', { target: 'document', capture: true })
  privacyShieldShowModal(event: CustomEvent): void {
    this.toggleConsentInfoModal(event.detail.id);
  }

  @Listen('usercentrics-consents-loaded', { target: 'document', capture: true })
  consentsLoaded(): void {
    if (!this.bannerVisible) return;
    addFocusEventListener(this.el);
    setAriaHiddenAttributes(this.el);
  }

  @Listen('usercentrics-consents-updated', { target: 'document', capture: true })
  consentsUpdated(): void {
    this.updateUsercentricsData();
    this.createPrivacyShields();
    this.removePrivacyShields();
    removeFocusEventListener(this.el);
    restoreAriaHiddenAttributes();
  }

  @Listen('trackEvent', { target: 'document', capture: true })
  trackEvents(event: CustomEvent<TRACKING_EVENT_TYPE>): void {
    ConsentHelper.trackEvent(this.UC, event.detail);
  }

  @Listen('toggleOverlay', { target: 'document', capture: true })
  toggleHandler(): void {
    this.toggleConsentInfoModal();
  }

  @Listen('toggleNotification', { target: 'document', capture: true })
  toggleNotification(): void {
    this.showNotification = false;
  }

  @Method()
  public async getServices(): Promise<Service[]> {
    return this.UC?.getServicesFullInfo();
  }

  @Method()
  public async updateServices(serviceId: string, status: boolean): Promise<void> {
    this.userDecision = this.updateUserDecision(serviceId, status);
    this.handlerSaveSelection();
  }

  @Method()
  public async changeLanguage(language: string): Promise<void> {
    this.UC.changeLanguage(language).then(() => {
      this.updateContent();
      this.dispatchCustomEvent(Events.CHANGE_LANGUAGE, { language });
    });
  }

  @Method()
  public async getConsents(): Promise<ConsentStatus[]> {
    return ConsentHelper.getAllConsents(this.categories);
  }

  @Method()
  public async getConsentsByCategory(): Promise<ConsentByCategory[]> {
    return ConsentHelper.getConsentsByCategory(this.categories, this.bannerVisible);
  }

  @Method()
  public async getLanguageSettings(): Promise<LanguageSettings> {
    return this.baseSettings?.language;
  }

  /*
   * Create iframe when consent is given and remove previous Privacy Shield overlay
   * Create privacy shield when consent is not given and remove previous iframes
   */
  @Method()
  public async togglePrivacyShield(
    source: string,
    container: Container = document,
    height?: number | string,
    width?: number | string,
  ): Promise<void> {
    const technologies: Technology[] = Technologies;
    technologies?.forEach(async (technology: Technology) => {
      const matchedTechnology = source?.match(technology.regex);
      if (!matchedTechnology) {
        return;
      }

      const isConsentGiven = await this.getConsentStatus(technology);
      const getIframe = () => container.querySelector(Constants.IFRAME_SELECTOR);
      const getPlaceholder = () => container.querySelector(Constants.PRIVACY_SHIELD_SELECTOR);

      if (isConsentGiven) {
        const iframeExists = !!getIframe();
        if (iframeExists) {
          return;
        }

        const iframe = this.createIframe(source, height, width);
        container.appendChild(iframe);

        const placeholderExists = !!getPlaceholder();
        if (!placeholderExists) {
          return;
        }

        container.removeChild(getPlaceholder());
      } else {
        const placeholderExists = !!getPlaceholder();
        if (placeholderExists) {
          return;
        }

        const privacyShield = this.createPrivacyShieldOverlay(technology, source, height, width);
        container.appendChild(privacyShield);

        const iframeExists = !!getIframe();
        if (!iframeExists) {
          return;
        }

        container.removeChild(getIframe());
      }
    });
  }

  /*
   * Replace iframes (youtube or google maps) for placeholders
   * when consent to the technology is not given
   */
  @Method()
  public async createPrivacyShields(element: Container = document): Promise<void> {
    const iframes: NodeListOf<HTMLIFrameElement> = element.querySelectorAll('iframe');
    iframes?.forEach((iframe: HTMLIFrameElement, index: number) => {
      const technologies: Technology[] = Technologies;
      technologies?.forEach(async (technology: Technology) => {
        const source = iframe.getAttribute(Constants.SOURCE_ATTRIBUTE);
        const matchedTechnology = source?.match(technology.regex);

        if (!matchedTechnology) {
          return;
        }

        const isConsentGiven = await this.getConsentStatus(technology);
        if (isConsentGiven) {
          return;
        }

        const privacyShield = this.createPrivacyShieldOverlay(
          technology,
          source,
          iframe.offsetHeight,
          iframe.offsetWidth,
          index + 1,
        );

        const parent: HTMLElement = iframe.parentElement;
        parent?.replaceChild(privacyShield, iframe);

        const current = element.querySelector(`#privacy-shield-${index + 1}`);
        current?.shadowRoot?.appendChild(iframe);
      });
    });
  }

  /*
   * Replace placeholders for iframes (youtube or google maps)
   * when consent to the technology is given
   */
  @Method()
  public async removePrivacyShields(element: Container = document): Promise<void> {
    const placeholders: NodeListOf<HTMLElement> = element.querySelectorAll(Constants.PRIVACY_SHIELD_SELECTOR);
    placeholders?.forEach((placeholder: HTMLElement) => {
      const technologies: Technology[] = Technologies;
      technologies?.forEach(async (technology: Technology) => {
        const iframe = placeholder.shadowRoot.querySelector('iframe');
        const matchedTechnology = iframe?.getAttribute(Constants.SOURCE_ATTRIBUTE)?.match(technology.regex);
        if (!matchedTechnology) {
          return;
        }

        const isConsentGiven = await this.getConsentStatus(technology);
        if (isConsentGiven) {
          const parent: HTMLElement = placeholder.parentElement;
          parent?.replaceChild(iframe, placeholder);
        }
      });
    });
  }

  @Method()
  public async updatePrivacyShieldContent(element: Container = document): Promise<void> {
    const privacyShields = element.querySelectorAll(Constants.PRIVACY_SHIELD_SELECTOR);
    privacyShields.forEach((privacyShield) => {
      privacyShield.setAttribute(Constants.LANGUAGE_ATTRIBUTE, this.currentLanguage);
      privacyShield.setAttribute(Constants.DIRECTION_ATTRIBUTE, this.direction);
      privacyShield.setAttribute(Constants.LEGAL_NAME_ATTRIBUTE, this.legalName);
      privacyShield.updateContent();
    });
  }

  @Method()
  public async getLabelsForPrivacyShield(
    technology: string,
    keyOverrides: LabelKeysOverrides,
  ): Promise<PrivacyShieldLabels> {
    return getLabelsToPlaceholder(this.currentLanguage, technology, this.labels, this.legalName, keyOverrides);
  }

  @Method()
  public async getDirection(): Promise<Direction> {
    return getTextDirection(this.currentLanguage);
  }

  private createPrivacyShieldOverlay(
    technology: Technology,
    source: string,
    height?: string | number,
    width?: string | number,
    index?: number,
  ): HTMLElement {
    const privacyShield = document.createElement(Constants.PRIVACY_SHIELD_SELECTOR);
    this.currentLanguage && privacyShield.setAttribute(Constants.LANGUAGE_ATTRIBUTE, this.currentLanguage);
    this.direction && privacyShield.setAttribute(Constants.DIRECTION_ATTRIBUTE, this.direction);
    this.labels && privacyShield.setAttribute(Constants.LABELS_ATTRIBUTE, `${JSON.stringify(this.labels)}`);
    this.legalName && privacyShield.setAttribute(Constants.LEGAL_NAME_ATTRIBUTE, this.legalName);
    technology && privacyShield.setAttribute(Constants.TECHNOLOGY_ATTRIBUTE, `${JSON.stringify(technology)}`);
    source && privacyShield.setAttribute(Constants.SOURCE_ATTRIBUTE, source);
    height && privacyShield.setAttribute(Constants.HEIGHT_ATTRIBUTE, `${height}`);
    width && privacyShield.setAttribute(Constants.WIDTH_ATTRIBUTE, `${width}`);
    index && privacyShield.setAttribute(Constants.ID_ATTRIBUTE, `privacy-shield-${index}`);

    return privacyShield;
  }

  private createIframe(source: string, height?: string | number, width?: string | number): HTMLElement {
    const iframe = document.createElement(Constants.IFRAME_SELECTOR);
    source && iframe.setAttribute(Constants.SOURCE_ATTRIBUTE, source);
    height && iframe.setAttribute(Constants.HEIGHT_ATTRIBUTE, `${height}`);
    width && iframe.setAttribute(Constants.WIDTH_ATTRIBUTE, `${width}`);

    return iframe;
  }

  private dispatchUsercentricsConsentsLoaded(): void {
    const consents = ConsentHelper.getAllConsents(this.categories);
    this.dispatchCustomEvent(Events.CONSENTS_LOADED, { consents });
  }

  private async initializeUC(): Promise<void> {
    const UC = new Usercentrics(this.settingsId, { storeServiceIdToNameMapping: true });

    const initialUIValues = await UC.init();

    this.UC = UC;
    this.categories = UC.getCategoriesBaseInfo();
    const consents = ConsentHelper.getAllConsents(this.categories);
    this.setConsentsInLocalStorage(consents);

    this.baseSettings = UC.getSettingsCore();
    this.settingsLabels = UC.getSettingsLabels();
    this.userDecision = ConsentHelper.getUserDecision(this.categories);

    try {
      await this.setInternalLabels();
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error));
    }

    this.createPrivacyShields();
    this.removePrivacyShields();
    this.createMutationObservers();
    this.bannerVisible = initialUIValues?.initialLayer === UI_LAYER.FIRST_LAYER;
    if (this.bannerVisible) {
      ConsentHelper.trackEvent(this.UC, TRACKING_EVENT_TYPE.CMP_SHOWN);
    }

    if (!this.isSettingsPage()) {
      this.dispatchUsercentricsConsentsLoaded();

      if (this.isSmallScreen()) {
        this.blockBodyScroll(this.bannerVisible);
      }
    }

    this.isPerformanceEnvironment();
  }

  private initializeAriaAttributes(): void {
    const banner: HTMLElement = this.el.shadowRoot.querySelector(Constants.BANNER_CONTENT_SELECTOR);
    if (!banner) return;

    const categoryToggleButtons: NodeListOf<HTMLElement> = banner.querySelectorAll(
      '[data-test="toggle-category-details"]',
    );
    categoryToggleButtons.forEach((toggleButton) => {
      const button: HTMLElement = toggleButton.shadowRoot?.querySelector('button');
      if (button && !button.hasAttribute('aria-expanded')) {
        button.setAttribute('aria-expanded', 'false');
      }
    });

    const detailsToggleButtons: NodeListOf<HTMLElement> = banner.querySelectorAll(
      '[data-test="toggle-cookie-details"]',
    );
    detailsToggleButtons.forEach((toggleButton) => {
      const button: HTMLElement = toggleButton.shadowRoot?.querySelector('button');
      if (button && !button.hasAttribute('aria-expanded')) {
        button.setAttribute('aria-expanded', 'false');
      }
    });
  }

  private isPerformanceEnvironment(): void {
    const queryString = window?.location?.search;
    const urlParams = new URLSearchParams(queryString);
    const performance = urlParams.get('pagespeed');

    if (performance && performance === 'true' && !this.isSettingsPage()) {
      this.handlerAcceptAll();
    } else {
      this.ucInitialized = true;
    }
  }

  private async getConsentStatus(technology: Technology): Promise<boolean> {
    const services = await this.UC?.getServicesFullInfo();

    return services?.some((service) => {
      const name = service?.name?.includes(technology?.id);
      const status = service?.consent?.status;
      return name && status;
    });
  }

  private handleVariantInitialization(): Promise<void> {
    if ((this.forceVariant && this.variant) || this.isSettingsPage()) {
      this.internalVariant = this.variant;
      return Promise.resolve();
    } else if (this.settingsId) {
      if (!this.bannerVisible) {
        return Promise.resolve();
      } else {
        return this.setInternalVariantAndName();
      }
    } else {
      this.variant = DEFAULT_VARIANT;
      return Promise.resolve();
    }
  }

  private isSettingsPage(): boolean {
    return this.variant === BannerVariant.SettingsPage;
  }

  private blockBodyScroll(blockScroll = false): void {
    if (!blockScroll) {
      document.body.classList.remove(Constants.FIXED_BODY_SELECTOR);
      return null;
    }
    document.body.classList.add(Constants.FIXED_BODY_SELECTOR);
    const style = document.createElement('style');
    style.textContent = `.${Constants.FIXED_BODY_SELECTOR} { overflow: hidden }`;
    document.head.appendChild(style);
  }

  private isSmallScreen(): boolean {
    return window.innerWidth < 768;
  }

  private setInternalVariantAndName(): Promise<void> {
    return getSettingData(this.settingsId)
      .then((response: SettingDataResponse): void => {
        const variant: BannerVariant = response?.variant;
        this.internalVariant = variant || DEFAULT_VARIANT;
        this.variant = variant || DEFAULT_VARIANT;
        this.legalName = response?.name || '';
      })
      .catch(() => {
        this.internalVariant = DEFAULT_VARIANT;
        this.variant = DEFAULT_VARIANT;
        this.legalName = '';
      });
  }

  private async setInternalLabels(): Promise<void> {
    const languageAvailables = this.baseSettings?.language?.available;
    const joinedLanguages = languageAvailables?.join(',');
    const params = {
      language: joinedLanguages,
      settingsId: this.settingsId,
    };

    const response: Response = await getLabels(params);
    const labels: LabelsResponse = response?.textLabels;
    const languages: LanguageResponse = response?.languages;
    const labelsByLanguage = [];
    labels.forEach((label: Label) => {
      const isOnLabels = labelsByLanguage.find((labelByLanguage) => {
        const languageKey = Object.keys(labelByLanguage);
        return languageKey[0] === label.language;
      });
      if (isOnLabels) {
        const index = labelsByLanguage.indexOf(isOnLabels);
        labelsByLanguage[index][label.language][label.textKey] = label.text;
      } else {
        labelsByLanguage.push({
          [label.language]: {
            [label.textKey]: label.text,
          },
        });
      }
    });
    this.languages = languages;
    this.labels = labelsByLanguage;
  }

  private handleCloseBanner(): void {
    if (this.internalVariant === BannerVariant.Information) {
      this.handlerAcceptAll();
    }
  }

  private handlerAcceptAll(collapsed = true): void {
    ConsentHelper.acceptAllConsents(this.UC)
      .then(() => {
        const event = collapsed ? TRACKING_EVENT_TYPE.ACCEPT_ALL_L1 : TRACKING_EVENT_TYPE.ACCEPT_ALL_L2;
        ConsentHelper.trackEvent(this.UC, event);
        this.updateConsents();
      })
      .catch((error) => {
        console.error(error || 'Error: There was an issue with accept all consents');
      });
  }

  private handlerDeny(): void {
    ConsentHelper.denyConsentsExceptRequired(this.UC)
      .then(() => {
        this.updateConsents();
        ConsentHelper.trackEvent(this.UC, TRACKING_EVENT_TYPE.DENY_ALL);
      })
      .catch((error) => {
        console.error(error || 'Error: There was an issue with deny consents');
      });
  }

  private handlerSaveSelection(collapsed = true): void {
    ConsentHelper.saveConsentSelection(this.UC, this.userDecision)
      .then(() => {
        const event = collapsed ? TRACKING_EVENT_TYPE.SAVE_L1 : TRACKING_EVENT_TYPE.SAVE_L2;
        ConsentHelper.trackEvent(this.UC, event);
        this.updateConsents();
      })
      .catch((error) => {
        console.error(error || 'Error: There was an issue with save consents');
      });
  }

  private updateUsercentricsData(): void {
    this.baseSettings = this.UC.getSettingsCore();
    this.settingsLabels = this.UC.getSettingsLabels();
    this.categories = this.UC.getCategoriesBaseInfo();
    this.userDecision = ConsentHelper.getUserDecision(this.categories);
  }

  private updateContent(): void {
    this.baseSettings = this.UC?.getSettingsCore();
    this.settingsLabels = this.UC?.getSettingsLabels();
    this.categories = this.UC?.getCategoriesBaseInfo();
    this.userDecision = ConsentHelper.getUserDecision(this.categories);
    this.currentLanguage = getLanguage(this.baseSettings);
    this.direction = getTextDirection(this.currentLanguage);
    this.bannerLabels = getLabelsToBanner(this.currentLanguage, this.labels);

    this.updatePrivacyShieldContent();
  }

  private createMutationObservers(): void {
    const config: MutationObserverInit = { attributes: false, childList: true, subtree: true };
    const callback = (mutationsList: Array<MutationRecord>): void => {
      mutationsList.forEach(() => {
        this.createPrivacyShields();
        this.removePrivacyShields();
      });
    };

    const observer: MutationObserver = new MutationObserver(callback);
    observer.observe(document.body, config);
  }

  private hideCustomBanner(): void {
    this.bannerVisible = false;
    this.blockBodyScroll();
  }

  private setConsentsInLocalStorage(consents: ConsentStatus[]) {
    const consentsForLocalStorage = ConsentHelper.consentsArrayToObjectConverter(consents);
    localStorage.setItem(Constants.LOCAL_STORAGE_CONSENTS, JSON.stringify(consentsForLocalStorage));
  }

  private updateConsents(showNotification = true): void {
    this.updateUsercentricsData();
    const consents = ConsentHelper.getAllConsents(this.categories);
    this.setConsentsInLocalStorage(consents);
    this.dispatchCustomEvent(Events.CONSENTS_UPDATED, { consents });
    if (this.internalVariant === BannerVariant.SettingsPage) {
      this.showNotification = showNotification;
      this.hideNotification();
    }
    this.hideCustomBanner();
  }

  private hideNotification(): void {
    setTimeout(() => {
      this.showNotification = false;
    }, 10000);
  }

  private async toggleConsentInfoModal(serviceID?: string): Promise<void> {
    const serviceModal: HTMLElement = document.querySelector(Constants.CONSENT_OVERLAY_SELECTOR);
    serviceModal?.parentNode.removeChild(serviceModal);

    if (serviceID) {
      if (this.bannerVisible && this.internalVariant !== BannerVariant.SettingsPage) {
        this.lastActiveElement = this.el.shadowRoot.activeElement as HTMLElement;
        removeFocusEventListener(this.el);
        restoreAriaHiddenAttributes();
      }
      const services = await this.UC?.getServicesFullInfo();
      const serviceData = services?.find((service) => service?.id === serviceID);
      const serviceCheckbox = this.el.shadowRoot.querySelector(
        `[data-test="handle-consent-change"][data-template-id="${serviceID}"]`,
      );
      const category = serviceCheckbox?.getAttribute(Constants.CATEGORY_SELECTOR);
      const overlay = document.createElement(Constants.CONSENT_OVERLAY_SELECTOR);

      overlay.setAttribute(Constants.DIRECTION_ATTRIBUTE, this.direction);
      overlay.setAttribute(Constants.SETTINGS_LABELS_ATTRIBUTE, `${JSON.stringify(this.settingsLabels)}`);
      overlay.setAttribute(Constants.SERVICE_DATA_ATTRIBUTE, `${JSON.stringify(serviceData)}`);
      overlay.setAttribute(Constants.CATEGORY_ATTRIBUTE, category);
      document.body.appendChild(overlay);
    } else {
      if (this.bannerVisible && this.internalVariant !== BannerVariant.SettingsPage) {
        setTimeout(() => {
          this.lastActiveElement?.focus();
        });
        addFocusEventListener(this.el);
        setAriaHiddenAttributes(this.el);
      }
    }
  }

  private updateUserDecision(id: string, status: boolean): UserDecision[] {
    return this.userDecision.map((service) => {
      if (service.serviceId === id) {
        service.status = status;
      }
      return service;
    });
  }

  private getStateForCheckbox(
    serviceCheckboxes: NodeListOf<Element>,
    checkedServiceCheckboxes: NodeListOf<Element>,
  ): string {
    let state = CheckboxState.Partial;
    if (serviceCheckboxes.length === checkedServiceCheckboxes.length) {
      state = CheckboxState.All;
    } else if (!checkedServiceCheckboxes.length) {
      state = CheckboxState.None;
    }
    return state;
  }

  private getStateForCategoryCheckbox(target: HTMLInputElement): string {
    const hasNoneClass = target.classList.contains(CheckboxState.None);
    const hastPartialClass = target.classList.contains(CheckboxState.Partial);

    return hasNoneClass || hastPartialClass ? CheckboxState.All : CheckboxState.None;
  }

  private setClassForCategoryCheckboxes(categoryCheckboxes: NodeListOf<Element>, state: string): void {
    categoryCheckboxes.forEach((checkbox) => {
      if (checkbox.getAttribute('disabled') == null) {
        Object.values(CheckboxState).forEach((previousState) => {
          checkbox.classList.remove(previousState);
        });
        checkbox.classList.add(state);
      }
    });
  }

  private updateServiceCheckboxes(serviceCheckboxes: NodeListOf<Element>, state: string): void {
    serviceCheckboxes.forEach((checkbox: HTMLInputElement) => {
      // Set checked value by state
      if (checkbox.getAttribute('disabled') == null) {
        checkbox.checked = state === CheckboxState.All;
      }
      // Update user decision by serviceId and status
      const serviceId: string = checkbox.getAttribute('data-template-id');
      const status = checkbox.checked;
      this.userDecision = this.updateUserDecision(serviceId, status);
    });
  }

  private setClassForSelectAllCheckbox(checkbox: HTMLInputElement): void {
    Object.values(CheckboxState).forEach((state) => {
      checkbox.classList.remove(state);
    });
    checkbox.classList.add(ConsentHelper.getCheckboxAllClass(this.userDecision));
  }

  private handleServiceChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const serviceId: string = target.getAttribute('data-template-id');
    const category: string = target.getAttribute(Constants.CATEGORY_SELECTOR);
    const status: boolean = target.checked;
    const banner: HTMLElement = this.el.shadowRoot.querySelector(
      `.cmm-cookie-banner.cmm-cookie-banner--${this.internalVariant}`,
    );
    const serviceCheckboxes: NodeListOf<Element> = banner.querySelectorAll(`
      [data-category-wrapper="${category}"] .consent-list input[type="checkbox"]`);
    const checkedServiceCheckboxes: NodeListOf<Element> = banner.querySelectorAll(`
      [data-category-wrapper="${category}"] .consent-list input[type="checkbox"]:checked`);
    const categoryCheckboxes: NodeListOf<Element> = banner.querySelectorAll(`
    .category-label input[type="checkbox"][data-category="${category}"]`);
    const selectAllCheckbox: HTMLInputElement = banner.querySelector('.select-all--label input[type="checkbox"]');

    const state: string = this.getStateForCheckbox(serviceCheckboxes, checkedServiceCheckboxes);
    this.setClassForCategoryCheckboxes(categoryCheckboxes, state);
    this.userDecision = this.updateUserDecision(serviceId, status);
    this.setClassForSelectAllCheckbox(selectAllCheckbox);
  }

  private handleCategoryChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const category: string = target.getAttribute(Constants.CATEGORY_SELECTOR);
    const banner: HTMLElement = this.el.shadowRoot.querySelector(
      `.cmm-cookie-banner.cmm-cookie-banner--${this.internalVariant}`,
    );
    const serviceCheckboxes: NodeListOf<Element> = banner.querySelectorAll(`
    [data-category-wrapper="${category}"] .consent-list input[type="checkbox"]`);
    const categoriesCheckboxes: NodeListOf<Element> = banner.querySelectorAll(`
      .category-label input[type="checkbox"][data-category="${category}"]`);
    const selectAllCheckbox: HTMLInputElement = banner.querySelector('.select-all--label input[type="checkbox"]');

    const state: string = this.getStateForCategoryCheckbox(target);
    this.setClassForCategoryCheckboxes(categoriesCheckboxes, state);
    this.updateServiceCheckboxes(serviceCheckboxes, state);
    this.setClassForSelectAllCheckbox(selectAllCheckbox);
  }

  private handleSelectAllChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const banner: HTMLElement = this.el.shadowRoot.querySelector(
      `.cmm-cookie-banner.cmm-cookie-banner--${this.internalVariant}`,
    );
    const categoriesCheckboxes: NodeListOf<Element> = banner.querySelectorAll(
      'input[type="checkbox"].cmm-checkbox-input--category',
    );
    const serviceCheckboxes: NodeListOf<Element> = banner.querySelectorAll('.consent-list input[type="checkbox"]');

    const state: string = this.getStateForCategoryCheckbox(target);
    this.setClassForCategoryCheckboxes(categoriesCheckboxes, state);
    this.updateServiceCheckboxes(serviceCheckboxes, state);
    this.setClassForSelectAllCheckbox(target);
  }

  private handleChangeLanguage(event: Event): void {
    const target = event.target as HTMLButtonElement;
    const language: string = target.getAttribute('data-language');
    this.UC.changeLanguage(language).then(() => {
      this.updateContent();
      this.dispatchCustomEvent(Events.CHANGE_LANGUAGE, { language });
    });
  }

  private dispatchCustomEvent(eventName: string, detail: Record<string, unknown> = {}): void {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail,
    });
    document?.dispatchEvent(event);
  }

  private scrollToDetails(category): void {
    const banner: HTMLElement = this.el.shadowRoot.querySelector(Constants.BANNER_CONTENT_SELECTOR);
    const wrapper: HTMLElement = banner?.querySelector('#cookie-settings');

    if (wrapper && !banner?.classList.contains(Constants.SETTINGS_EXPANDED_SELECTOR)) {
      banner.classList.add(Constants.SETTINGS_EXPANDED_SELECTOR);
    }

    if (wrapper) {
      const checkbox: HTMLElement = wrapper.querySelector(`.category-item[data-category-wrapper="${category}"]`);
      this.scrollToElement(banner, checkbox);
    }
  }

  private handlerToggleDetails(): void {
    const banner: HTMLElement = this.el.shadowRoot.querySelector(Constants.BANNER_CONTENT_SELECTOR);
    const wrapper: HTMLElement = banner?.querySelector('#cookie-settings');
    const toggleButtons: NodeListOf<HTMLElement> = banner.querySelectorAll('[data-test="toggle-cookie-details"]');

    if (wrapper && toggleButtons.length > 0) {
      const isExpanded = banner.classList.toggle(Constants.SETTINGS_EXPANDED_SELECTOR);

      toggleButtons.forEach((toggleButtonW) => {
        const button: HTMLElement = toggleButtonW.shadowRoot?.querySelector('button');
        if (button) {
          button.setAttribute('aria-expanded', String(isExpanded));
        }
      });

      if (isExpanded) {
        const checkbox: HTMLElement = wrapper.querySelector('.select-all-item');
        this.scrollToElement(banner, checkbox);
      }
    }
  }

  private scrollToElement(banner: HTMLElement, element: HTMLElement): void {
    setTimeout(() => {
      if (banner?.scrollTo) {
        banner.scrollTo({
          top: element?.offsetTop - 20,
          left: 0,
          behavior: 'smooth',
        });
      }
      setTimeout(() => {
        setFocusFirstElement(element);
      }, 300);
    }, 300);
  }

  private toggleCategoryDetails(category: BaseCategory): void {
    const label = category?.label;
    const banner: HTMLElement = this.el.shadowRoot.querySelector(Constants.BANNER_CONTENT_SELECTOR);
    const categoryItem: HTMLElement = banner.querySelector(`.category-item[data-category-wrapper="${label}"]`);
    const toggleButton: HTMLElement = categoryItem
      .querySelector('[data-test="toggle-category-details"]')
      ?.shadowRoot?.querySelector('button');

    if (categoryItem && toggleButton) {
      const isExpanded = categoryItem.classList.toggle('category-item--expanded');
      toggleButton.setAttribute('aria-expanded', String(isExpanded));
    }
  }

  private getCookieSettingsLabels(): CookieSettingsLabels {
    // Labels from external tool
    const labels = this.settingsLabels as DefaultLabels;
    const acceptAll = labels?.buttons?.acceptAll;
    const save = labels?.buttons?.save;
    // Labels from our backend API
    const close = this.bannerLabels?.close;
    const consentChanged = this.bannerLabels?.consentChanged;
    const hideDetails = this.bannerLabels?.hideDetails;
    const selectAll = this.bannerLabels?.selectAll;
    const showDetails = this.bannerLabels?.showDetails;

    return {
      acceptAll,
      close,
      consentChanged,
      hideDetails,
      save,
      selectAll,
      showDetails,
    };
  }

  private getHeadingContent(): HeadingContent {
    const labels = this.settingsLabels as DefaultLabels;
    const title = labels?.firstLayer?.title;
    const wholeDescription = labels?.firstLayer?.description?.default;
    // 1. Clean description from UC formatter
    const cleanedDescription = stringCleaner(wholeDescription);
    // 2. Split description by paragraph
    const splitDescription = cleanedDescription?.split('<br>');
    // 3. Convert hint in html format
    const description = hintConverter(splitDescription);

    return {
      title,
      description,
    };
  }

  private getButtonLabels(): ButtonLabels {
    // Labels from external tool
    const labels = this.settingsLabels as DefaultLabels;
    const acceptAll = labels?.buttons?.acceptAll;
    const deny = labels?.buttons?.denyAll;
    const save = labels?.buttons?.save;
    const toggle = labels?.buttons?.showSecondLayer;
    // Labels from our backend API
    const close = this.bannerLabels?.close;
    const showDetails = this.bannerLabels?.showDetails;

    return {
      acceptAll,
      close,
      deny,
      save,
      showDetails,
      toggle,
    };
  }

  private getFooterLinks(): Array<BannerLink> {
    const legalPagesDefined = this.legalPages && this.legalPages.length;
    const settings = this.settingsLabels as DefaultLabels;
    const privacyLabel = settings?.links?.privacyPolicy?.label;
    const imprintLabel = settings?.links?.imprint?.label;
    const internalImprintLink = this.internalLegalPages?.[this.currentLanguage]?.imprint;
    const internalPrivacyPolicyLink = this.internalLegalPages?.[this.currentLanguage]?.privacyPolicy;

    const privacyLink =
      legalPagesDefined && internalImprintLink ? internalImprintLink : settings?.links?.privacyPolicy?.url;
    const imprintLink =
      legalPagesDefined && internalPrivacyPolicyLink ? internalPrivacyPolicyLink : settings?.links?.imprint?.url;

    const privacyPolicy = { url: privacyLink, label: privacyLabel, event: TRACKING_EVENT_TYPE.PRIVACY_POLICY_LINK };
    const imprint = { url: imprintLink, label: imprintLabel, event: TRACKING_EVENT_TYPE.IMPRINT_LINK };

    return [privacyPolicy, imprint];
  }

  private bannerVisibility(): string {
    return this.bannerVisible ? 'visible' : '';
  }

  public render(): JSX.Element {
    return (
      <Host>
        {this.internalVariant && this.ucInitialized && (
          <div
            data-nosnippet
            dir={this.direction}
            class={`cmm-cookie-banner cmm-cookie-banner--${this.internalVariant}`}
          >
            {this.internalVariant !== BannerVariant.SettingsPage ? (
              <div
                class={`cmm-cookie-banner__wrapper ${this.bannerVisibility()}`}
                aria-label={this.getHeadingContent().title}
                aria-modal="true"
                role="dialog"
                tabindex={0}
              >
                <div
                  class="cmm-cookie-banner__overlay"
                  data-test="handle-close-banner"
                  onClick={(): void => this.handleCloseBanner()}
                ></div>
                <div class="cmm-cookie-banner__content" tabIndex={0}>
                  <cmm-languages-list
                    languages={this.languages}
                    languageSelected={getLanguage(this.baseSettings)}
                    handleChangeLanguage={(event: MouseEvent): void => this.handleChangeLanguage(event)}
                  ></cmm-languages-list>

                  <cmm-heading-section content={this.getHeadingContent()}></cmm-heading-section>

                  {this.internalVariant === BannerVariant.Categories && (
                    <cmm-category-overview
                      categories={this.categories}
                      handleCategoryChange={(event: MouseEvent): void => this.handleCategoryChange(event)}
                      scrollToDetails={(categoryLabel: string): void => this.scrollToDetails(categoryLabel)}
                    ></cmm-category-overview>
                  )}

                  <cmm-buttons-wrapper
                    labels={this.getButtonLabels()}
                    variant={this.internalVariant}
                    handlerAcceptAll={(): void => this.handlerAcceptAll()}
                    handlerDeny={(): void => this.handlerDeny()}
                    handlerToggleDetails={(): void => this.handlerToggleDetails()}
                    handlerSaveSelection={(): void => this.handlerSaveSelection()}
                  ></cmm-buttons-wrapper>

                  <cmm-footer-links links={this.getFooterLinks()}></cmm-footer-links>

                  <cmm-cookie-settings
                    categories={this.categories}
                    direction={this.direction}
                    labels={this.getCookieSettingsLabels()}
                    showNotification={this.showNotification}
                    userDecision={this.userDecision}
                    variant={this.internalVariant}
                    handleCategoryChange={(event: MouseEvent): void => this.handleCategoryChange(event)}
                    handlerAcceptAll={(): void => this.handlerAcceptAll(false)}
                    handlerSaveSelection={(): void => this.handlerSaveSelection(false)}
                    handlerToggleDetails={(): void => this.handlerToggleDetails()}
                    handleSelectAllChange={(event: MouseEvent): void => this.handleSelectAllChange(event)}
                    handleServiceChange={(event: MouseEvent): void => this.handleServiceChange(event)}
                    toggleCategoryDetails={(category: BaseCategory): void => this.toggleCategoryDetails(category)}
                    toggleConsentInfoModal={(id: string): Promise<void> => this.toggleConsentInfoModal(id)}
                  ></cmm-cookie-settings>
                </div>
              </div>
            ) : (
              <div class="cmm-cookie-banner__settings-page">
                <cmm-heading-section content={this.getHeadingContent()}></cmm-heading-section>
                <cmm-cookie-settings
                  categories={this.categories}
                  direction={this.direction}
                  labels={this.getCookieSettingsLabels()}
                  showNotification={this.showNotification}
                  userDecision={this.userDecision}
                  variant={this.internalVariant}
                  handleCategoryChange={(event: MouseEvent): void => this.handleCategoryChange(event)}
                  handlerAcceptAll={(): void => this.handlerAcceptAll(false)}
                  handlerSaveSelection={(): void => this.handlerSaveSelection(false)}
                  handlerToggleDetails={(): void => this.handlerToggleDetails()}
                  handleSelectAllChange={(event: MouseEvent): void => this.handleSelectAllChange(event)}
                  handleServiceChange={(event: MouseEvent): void => this.handleServiceChange(event)}
                  toggleCategoryDetails={(category: Category): void => this.toggleCategoryDetails(category)}
                  toggleConsentInfoModal={(id: string): Promise<void> => this.toggleConsentInfoModal(id)}
                ></cmm-cookie-settings>
              </div>
            )}
          </div>
        )}
      </Host>
    );
  }
}
